import React from 'react';
import { TimerPhase, PlayerRole } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';
import { useSocket } from '../../contexts/SocketContext';

interface TimerDisplayProps {
  timer: {
    phase: TimerPhase;
    timeRemaining: number;
    timerEndTime: number;
  };
}

export const TimerDisplay: React.FC<TimerDisplayProps> = ({ timer }) => {
  const t = useTranslations();
  const { myRole, gameState, myPlayerId } = useSocket();
  const [timeRemaining, setTimeRemaining] = React.useState(() => 
    Math.max(0, Math.ceil((timer.timerEndTime - Date.now()) / 1000))
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((timer.timerEndTime - Date.now()) / 1000));
      setTimeRemaining(remaining);
    }, 100);

    return () => clearInterval(interval);
  }, [timer.timerEndTime]);

  const isExplanationPhase = timer.phase === TimerPhase.EXPLANATION_DISPLAY;
  const isPreparationPhase = timer.phase === TimerPhase.PREPARATION;

  if (!isExplanationPhase && !isPreparationPhase) {
    return null;
  }

  const currentRole = myRole || (gameState && myPlayerId 
    ? (() => {
        const role = gameState.room.players.find(p => p.id === myPlayerId)?.role;
        if (typeof role === 'string') {
          if (role === 'detective') return PlayerRole.DETECTIVE;
          if (role === 'deadpan') return PlayerRole.DEADPAN;
          if (role === 'fact_speaker') return PlayerRole.FACT_SPEAKER;
        }
        return (role as unknown) as PlayerRole | null;
      })()
    : null);
  const isDetective = currentRole === PlayerRole.DETECTIVE;
  const phaseText = isExplanationPhase 
    ? (isDetective ? t.games.deadpan.timerOthersReadingExplanation : t.games.deadpan.timerExplanationDisplay)
    : (isDetective ? t.games.deadpan.timerOthersPreparing : t.games.deadpan.timerPreparation);

  const phaseColor = isExplanationPhase 
    ? 'from-green-500 to-emerald-500' 
    : 'from-blue-500 to-indigo-500';

  return (
    <div className={`w-full bg-gradient-to-r ${phaseColor} text-white rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 shadow-lg flex flex-col items-center`}>
      <div className="text-center w-full">
        <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2">{phaseText}</div>
        <div className="text-4xl sm:text-5xl font-bold">
          {timeRemaining}
        </div>
      </div>
    </div>
  );
};

